import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { UserCell } from '@/features/vfo/components';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { ApiCaseResponseV2, ApiParticipantResponse } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

type MembersTabProps = {
  participants: ApiParticipantResponse[];
  caseData?: ApiCaseResponseV2;
  onOpenCase?: () => void;
};

export const MembersTab: FC<MembersTabProps> = ({ caseData, participants, onOpenCase }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChatInfo' });

  const modals = useModalsStore();
  const isFounder = useIsFounder();
  const { getByProfileId } = useMembers();

  const members = useMemo(() => {
    return participants
      .map((participant) => getByProfileId(participant.profileId))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [participants]);

  return (
    <Container>
      {isFounder && caseData && (
        <CaseInfo>
          <Button
            style={{
              flexShrink: 0,
            }}
            type="default"
            size="small"
            onClick={() => {
              modals.openModal({
                type: 'AddMembersToCaseModal',
                caseId: caseData.id,
              });
            }}
          >
            {t('addMemberToCase')}
          </Button>
          <p>{t('whenMemberAdded')}</p>
        </CaseInfo>
      )}
      <MembersList>
        {members.map((member) => (
          <UserCell
            onOpenCase={() => {
              modals.closeModal();
              onOpenCase?.();
            }}
            key={member.profileId}
            member={member}
          />
        ))}
      </MembersList>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  color: ${COLORS.systemDark200};
  font-size: 12px;
  line-height: 16px;
  overflow: auto;
  max-height: calc(100vh - 420px);
`;

const CaseInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
